import React, { useState } from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import { TEXT_CONSTANTS } from "../../../../utils/text-constants/textConstants";
import Button from "../../../../component-lib/Molecules/button/Button";
import Input from "../../../../component-lib/Atoms/Input/Input";
import { ADD_GROUP_FIELDS } from "../constants";
import { useDispatch, useSelector } from "react-redux";
import {
  hideLoader,
  showLoader,
} from "../../../../component-lib/Molecules/loader/LoaderSlice";
import { createGroup, updateGroup } from "../../taskSaga";
import { componentKey, setCloseCreateGroupDialog } from "../../taskSlice";
import { getLazyLoadingApiResponse, getLazyLoadingSearchApiResponse } from "../../../../utils/constantsFunctions";
import EventEHRMultiSelectWithSearch from "../../../../component-lib/Atoms/Select/EventEHRMultiSelectWithSearch";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    BorderAllRounded: "8px",
    Height: "300px",
    width: "708px",
    maxWidth: "none",
  },
}));

interface CreateGroupDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  handleCreateGroup: (formData: any) => void;
  defaultGroupData?: any; // For edit mode
}

const CreateGroupDialog: React.FC<CreateGroupDialogProps> = ({
  isOpen,
  handleClose,
  handleCreateGroup,
  defaultGroupData,
}) => {
  const dispatch = useDispatch();
  const { closeCreateGroupDialog } = useSelector(
    (state: any) => state[componentKey]
  );
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);

  // Simple flag to prevent multiple initial API calls
  const initialLoadComplete = React.useRef(false);

  // LAZY LOADING STATES FOR USER DATA
  const [pageCountForGetUsers, setPageCountForGetUsers] = useState(1);
  const [dropdownSearchForGetUsers, setDropdownSearchForGetUsers] = useState("");
  const [dropdownOptionsForGetUsers, setDropdownOptionsForGetUsers] = useState<any>([]);
  const [resetFlagForGetUsers, setResetFlagForGetUsers] = useState(false);

  const validationSchema = Yup.object().shape({
    [ADD_GROUP_FIELDS?.GROUP_NAME]: Yup.string().required(
      "Group Name is required"
    ),
    [ADD_GROUP_FIELDS?.USER_NAME]: Yup.array()
      .min(1, "At least one user must be selected")
      .required("This field is required"),
  });

  // Load initial data only once
  React.useEffect(() => {
    if (!initialLoadComplete.current) {
      getDropdownListForUsers(1, true);
      initialLoadComplete.current = true;
    }
  }, []); // Run only once on mount

  // LAZY LOADING USE EFFECTS
  React.useEffect(() => {
    if (initialLoadComplete.current && !resetFlagForGetUsers) {
      if (dropdownSearchForGetUsers && dropdownSearchForGetUsers.length > 0) {
        getDropdownSearchListForUsers(dropdownSearchForGetUsers);
      } else if (pageCountForGetUsers > 1) {
        getDropdownListForUsers(pageCountForGetUsers);
      }
    }
  }, [dropdownSearchForGetUsers, pageCountForGetUsers, resetFlagForGetUsers]);

  React.useEffect(() => {
    if (resetFlagForGetUsers) {
      resetListForUsers();
    }
  }, [resetFlagForGetUsers]);

  React.useEffect(() => {
    if (closeCreateGroupDialog) {
      dispatch(setCloseCreateGroupDialog(false));
      handleClose();
    }
  }, [closeCreateGroupDialog]);

  const resetListForUsers = () => {
    setDropdownSearchForGetUsers("");
    setPageCountForGetUsers(1);
    getDropdownListForUsers(1, true);
  };

  // IF SEARCH VALUE THEN WE ARE GIVEN SOME TIMER
  const handleSearchedValueChange = (data: any) => {
    if (timer) {
      clearTimeout(timer); // Clear the previous timer
    }
    // Set a new timer with your desired delay (e.g., 500ms)
    const newTimer = setTimeout(() => {
      setDropdownSearchForGetUsers(data);
    }, 500); // Adjust the delay as needed (in milliseconds)

    setTimer(newTimer); // Store the timer ID for cleanup
  };

  // GET DROPDOWN LIST FOR USERS
  const getDropdownListForUsers = async (pageCount: any, reset = false) => {
    dispatch(showLoader());
    try {
      const response = await getLazyLoadingApiResponse("user", pageCount, 6, {
        isTaskAssignment: true,
      });
      const usersList = response?.data?.users || [];
      const mappedUserData = usersList
        .map((user: any) => ({
          label: `${user?.firstName} ${user?.lastName}`,
          value: String(user?.id),
        }));

      if (mappedUserData && mappedUserData.length > 0) {
        if (reset) {
          setDropdownOptionsForGetUsers(mappedUserData);
          setResetFlagForGetUsers(false);
        } else if (!dropdownOptionsForGetUsers.includes(mappedUserData[0])) {
          let options = dropdownOptionsForGetUsers.concat(mappedUserData);
          setDropdownOptionsForGetUsers(options);
        }
      }
    } catch (error) {
      console.error("Error fetching user list:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  // GET DROPDOWN SEARCH LIST FOR USERS
  const getDropdownSearchListForUsers = async (search: string) => {
    dispatch(showLoader());
    try {
      const response = await getLazyLoadingSearchApiResponse("user", search, "name", {
        isTaskAssignment: true,
      });
      const usersList = response?.data?.users || [];
      const mappedUserData = usersList
        .map((user: any) => ({
          label: `${user?.firstName} ${user?.lastName}`,
          value: String(user?.id),
        }));

      if (mappedUserData?.length) {
        setDropdownOptionsForGetUsers(mappedUserData);
      }
    } catch (error) {
      console.error("Error fetching search user list:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  return (
    <BootstrapDialog
      onClose={(event: any, reason: string) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
    >
      <Formik
        initialValues={{
          [ADD_GROUP_FIELDS?.GROUP_NAME]: defaultGroupData?.name || "",
          [ADD_GROUP_FIELDS?.USER_NAME]: defaultGroupData?.users || [],
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          try {
            const selectedUsers = values[ADD_GROUP_FIELDS.USER_NAME];
            // Extract a clean list of user ids, dropping any empty/id-less
            // entries (e.g. a placeholder chip for a deleted member) so we
            // never serialize an object to "[object Object]".
            const buildUserIds = (list: any) =>
              Array.isArray(list)
                ? list
                    .map((user: any) =>
                      user && typeof user === "object" ? user?.value : user
                    )
                    .filter(
                      (id: any) => id !== undefined && id !== null && id !== ""
                    )
                    .map((id: any) => String(id))
                : [];
            const payload = {
              name: values[ADD_GROUP_FIELDS.GROUP_NAME],
              users: buildUserIds(selectedUsers),
            };

            if (defaultGroupData?.id) {
              // Edit mode - update existing group
              const updatePayload = {
                name: values[ADD_GROUP_FIELDS.GROUP_NAME],
                userIds: buildUserIds(selectedUsers),
              };
              const groupUpdatePayload = {
                apiPayload: updatePayload,
                groupId: defaultGroupData.id,
              };
              dispatch(updateGroup(groupUpdatePayload));
            } else {
              // Create mode - create new group
              dispatch(createGroup(payload));
            }
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          values,
          touched,
          setFieldValue,
          handleBlur,
          handleSubmit,
        }: any) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="customized-dialog-title"
              >
                {defaultGroupData?.id ? "Edit Task Group" : "Create Task Group"}
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              {/* MAIN CONTENT */}
              <DialogContent>
                <div
                  style={{
                    overflow: "auto",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <div className="pt-4">
                    <Input
                      textFieldLabel="Group Name"
                      maxLength={20}
                      size="small"
                      name={ADD_GROUP_FIELDS.GROUP_NAME}
                      value={values[ADD_GROUP_FIELDS.GROUP_NAME]}
                      onChangeCb={(e) => {
                        setFieldValue(
                          ADD_GROUP_FIELDS.GROUP_NAME,
                          e.target.value
                        );
                      }}
                      onBlur={handleBlur}
                      error={
                        touched[ADD_GROUP_FIELDS.GROUP_NAME] &&
                        Boolean(errors[ADD_GROUP_FIELDS.GROUP_NAME])
                      }
                      helperText={
                        touched[ADD_GROUP_FIELDS.GROUP_NAME]
                          ? errors[ADD_GROUP_FIELDS.GROUP_NAME]
                          : undefined
                      }
                    />
                  </div>
                  <div className="pt-4">
                    <EventEHRMultiSelectWithSearch
                      top100Films={dropdownOptionsForGetUsers}
                      handleSearch={(e: any) => {
                        handleSearchedValueChange(e.target.value);
                      }}
                      values={values[ADD_GROUP_FIELDS.USER_NAME]}
                      handleChange={(data, selected) => {
                        setFieldValue(
                          ADD_GROUP_FIELDS.USER_NAME,
                          selected
                        );
                      }}
                      setPageCount={(data) => {
                        setPageCountForGetUsers(data);
                      }}
                      setResetFlag={(data) => {
                        setResetFlagForGetUsers(data);
                      }}
                      onBlur={handleBlur}
                      error={
                        touched[ADD_GROUP_FIELDS.USER_NAME] &&
                        Boolean(errors[ADD_GROUP_FIELDS.USER_NAME])
                      }
                      helperText={
                        touched[ADD_GROUP_FIELDS.USER_NAME]
                          ? errors[ADD_GROUP_FIELDS.USER_NAME]
                          : undefined
                      }
                    />
                  </div>
                </div>
              </DialogContent>

              {/* ACTION BUTTON */}
              <DialogActions style={{ padding: "24px" }}>
                <Button
                  label={TEXT_CONSTANTS?.CANCEL}
                  variant="outlined"
                  onClickCb={handleClose}
                  style={{
                    width: "120px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                    backgroundColor: "white",
                    border: "none",
                    color: "#393939",
                  }}
                />
                <Button
                  type="submit"
                  label={defaultGroupData?.id ? "Update" : "Save"}
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    width: "200px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                  }}
                ></Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
};

export default CreateGroupDialog;
